import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Avatar,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from 'omaha-design-system';
import { SwipeableDrawer } from '@mui/material';
import { SportsbookLogo } from './SportsbookLogo';
import {
  Trash as DeleteIcon,
  ArrowSquareOut as OpenInNewIcon,
} from '@phosphor-icons/react';
import { IRootState } from '../../../store/reducer';
import {
  removeFromBetSlip,
  clearBetSlip,
  setBetType,
} from '../store/slice';
import { BetSlipItem } from '../data/types';
import { calculateParlayStats } from '../data/probability';
import { getBetSlipDeepLinks, isActualMobileDevice, getPlatformDeepLink, openBetSlipDeepLinks } from '../data/deeplinkHelper';

interface BetSlipBottomSheetProps {
  open: boolean;
  onClose: () => void;
}

// Single bet card component
const BetCard: React.FC<{
  bet: BetSlipItem;
  onRemove: () => void;
  onOpenSportsbook?: () => void;
  compact?: boolean;
}> = ({ bet, onRemove, onOpenSportsbook, compact = false }) => (
  <Box
    sx={{
      backgroundColor: '#212223',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: '8px',
      p: 2,
    }}
  >
    <Stack direction="row" spacing={2} alignItems="flex-start">
      {/* Avatar */}
      <Avatar
        src={bet.avatar}
        sx={{ width: compact ? 24 : 32, height: compact ? 24 : 32 }}
      >
        {bet.playerName.split(' ').map((n) => n[0]).join('')}
      </Avatar>

      {/* Content */}
      <Stack sx={{ flex: 1 }} spacing={0.5}>
        {/* Player name row with action icons */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '13.33px',
              lineHeight: 1.5,
              color: '#F2F2F3',
            }}
          >
            {bet.playerName}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {onOpenSportsbook && (
              <IconButton
                onClick={onOpenSportsbook}
                sx={{
                  color: '#42A5F5',
                  p: 0.25,
                  '&:hover': { color: '#1976d2' },
                }}
              >
                <OpenInNewIcon size={20} />
              </IconButton>
            )}
            <IconButton
              onClick={onRemove}
              sx={{
                color: '#F2F2F3',
                p: 0.25,
              }}
            >
              <DeleteIcon size={20} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Market info row */}
        <Stack direction="row" justifyContent="space-between">
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 400,
              fontSize: '13.33px',
              lineHeight: 1.5,
              color: 'rgba(242, 242, 243, 0.7)',
            }}
          >
            {bet.stat} {bet.side === 'Under' ? 'u' : 'o'}{bet.bookLine}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                lineHeight: 1.5,
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              {bet.odds}
            </Typography>
            <SportsbookLogo book={bet.book} bookIcon={bet.bookLogo} size={18} borderRadius="3px" />
          </Stack>
        </Stack>

        {/* Projection and EV row */}
        <Stack direction="row" justifyContent="space-between">
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 400,
              fontSize: '13.33px',
              lineHeight: 1.5,
              color: '#42A5F5',
            }}
          >
            Proj: {bet.projection}
          </Typography>
          {bet.evPct !== null && (
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                lineHeight: 1.5,
                color: '#66BB6A',
              }}
            >
              {bet.evPct >= 0 ? '+' : ''}{bet.evPct.toFixed(1)}% EV
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  </Box>
);

// Parlay card component
const ParlayCard: React.FC<{
  bets: BetSlipItem[];
  combinedOdds: number;
  trueProb: number | null;
  parlayEv: number | null;
  onRemove: (index: number) => void;
  onClearAll: () => void;
}> = ({ bets, combinedOdds, trueProb, parlayEv, onClearAll }) => {
  const book = bets[0]?.book || '';

  return (
    <Box
      sx={{
        backgroundColor: '#212223',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '8px',
        p: 2,
      }}
    >
      <Stack spacing={1}>
        {/* Parlay header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: 1.5,
              color: '#F2F2F3',
            }}
          >
            {bets.length}-Leg Parlay - {book}
          </Typography>
          <IconButton
            onClick={onClearAll}
            sx={{ color: '#F2F2F3', p: 0.25 }}
          >
            <DeleteIcon size={20} />
          </IconButton>
        </Stack>

        {/* Combined odds row */}
        <Stack direction="row" justifyContent="space-between">
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 400,
              fontSize: '13.33px',
              color: 'rgba(242, 242, 243, 0.7)',
            }}
          >
            Combined Odds
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 400,
              fontSize: '13.33px',
              color: '#F2F2F3',
            }}
          >
            {combinedOdds > 0 ? '+' : ''}{combinedOdds} {book}
          </Typography>
        </Stack>

        {/* True % row */}
        {trueProb !== null && (
          <Stack direction="row" justifyContent="space-between">
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              Model Win %
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                color: '#42A5F5',
              }}
            >
              {trueProb.toFixed(2)}%
            </Typography>
          </Stack>
        )}

        {/* Parlay EV row */}
        {parlayEv !== null && (
          <Stack direction="row" justifyContent="space-between">
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              Parlay EV
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                color: '#66BB6A',
              }}
            >
              {parlayEv >= 0 ? '+' : ''}{parlayEv.toFixed(1)}%
            </Typography>
          </Stack>
        )}

        {/* SGP Correlation Disclaimer */}
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontWeight: 400,
            fontSize: '11px',
            fontStyle: 'italic',
            color: 'rgba(242, 242, 243, 0.5)',
            textAlign: 'center',
            pt: 1,
          }}
        >
          Note: Model Win % and EV do not account for SGP correlation.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

        {/* Individual legs */}
        {bets.map((bet) => (
          <Stack key={bet.id} direction="row" spacing={1} alignItems="flex-start">
            <Avatar
              src={bet.avatar}
              sx={{ width: 24, height: 24 }}
            >
              {bet.playerName.split(' ').map((n) => n[0]).join('')}
            </Avatar>
            <Stack sx={{ flex: 1 }} spacing={0.5}>
              <Typography
                sx={{
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: '13.33px',
                  color: '#F2F2F3',
                }}
              >
                {bet.playerName}
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 400,
                    fontSize: '13.33px',
                    color: 'rgba(242, 242, 243, 0.7)',
                  }}
                >
                  {bet.stat} {bet.side === 'Under' ? 'u' : 'o'}{bet.bookLine}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    sx={{
                      fontFamily: '"Instrument Sans", sans-serif',
                      fontWeight: 400,
                      fontSize: '13.33px',
                      color: 'rgba(242, 242, 243, 0.7)',
                    }}
                  >
                    {bet.odds}
                  </Typography>
                  <SportsbookLogo book={bet.book} bookIcon={bet.bookLogo} size={16} borderRadius="3px" />
                </Stack>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontFamily: '"Instrument Sans", sans-serif',
                    fontWeight: 400,
                    fontSize: '13.33px',
                    color: '#42A5F5',
                  }}
                >
                  Proj: {bet.projection}
                </Typography>
                {bet.evPct !== null && (
                  <Typography
                    sx={{
                      fontFamily: '"Instrument Sans", sans-serif',
                      fontWeight: 400,
                      fontSize: '13.33px',
                      color: '#66BB6A',
                    }}
                  >
                    {bet.evPct >= 0 ? '+' : ''}{bet.evPct.toFixed(1)}% EV
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export const BetSlipBottomSheet: React.FC<BetSlipBottomSheetProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { betSlip, betType } = useSelector((s: IRootState) => s.stokasticProps);

  // Check if all bets are from same sportsbook (required for parlay)
  const allBooks = new Set(betSlip.map((bet) => bet.book));
  const isSingleBook = allBooks.size <= 1;
  const canParlay = betSlip.length >= 2 && isSingleBook;

  // Calculate parlay odds
  const { combinedOdds, trueProb, parlayEv } =
    betType === 'PARLAY' && canParlay
      ? calculateParlayStats(betSlip)
      : { combinedOdds: 0, trueProb: null, parlayEv: null };

  const handleRemove = (index: number) => {
    dispatch(removeFromBetSlip(index));
  };

  // Get deep links grouped by sportsbook for display
  const deepLinks = useMemo(() => {
    const isMobile = isActualMobileDevice();
    return getBetSlipDeepLinks(betSlip, isMobile);
  }, [betSlip]);

  const handleClearSlip = () => {
    dispatch(clearBetSlip());
    onClose();
  };

  const handleOpenSportsbook = (url: string) => {
    window.open(url, '_blank');
  };

  // Get deep link for a single bet
  const getBetDeepLink = (bet: BetSlipItem): string | null => {
    const isMobile = isActualMobileDevice();
    return getPlatformDeepLink([bet], isMobile);
  };

  const handleBetTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: 'STRAIGHT' | 'PARLAY' | null
  ) => {
    if (newType) {
      dispatch(setBetType(newType));
    }
  };

  const getPlaceButtonText = () => {
    if (betType === 'PARLAY' && canParlay) {
      return `Place ${betSlip.length}-Leg Parlay`;
    }
    return `Place ${betSlip.length} Straight Bet${betSlip.length > 1 ? 's' : ''}`;
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}} // Required prop - we don't need to handle open since parent controls it
      disableSwipeToOpen={true} // Only allow swipe to close, not open
      swipeAreaWidth={0} // Disable swipe-to-open area
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: '#343536',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderBottom: 'none',
        }}
      >
        {/* Grabber */}
        <Box
          sx={{
            width: 40,
            height: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            borderRadius: 2,
            margin: '12px auto 8px',
            flexShrink: 0,
          }}
        />

        {/* Header with title and Clear Slip button */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 2, flexShrink: 0 }}
        >
          <Typography
            sx={{
              fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
              fontWeight: 900,
              fontSize: '23.04px',
              lineHeight: 1.248,
              color: '#F2F2F3',
            }}
          >
            Bet Slip
          </Typography>
          {betSlip.length > 0 && (
            <Button
              variant="text"
              onClick={handleClearSlip}
              sx={{
                color: '#EF5350',
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '13.33px',
                textTransform: 'none',
                padding: '5.55px',
                minWidth: 'auto',
              }}
            >
              Clear Slip
            </Button>
          )}
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)', mx: 2 }} />

        {/* Fixed Header Content */}
        <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
          <Stack spacing={2}>

            {/* Toggle buttons */}
            {betSlip.length >= 2 && (
              <Stack spacing={1}>
                <ToggleButtonGroup
                  value={betType}
                  exclusive
                  onChange={handleBetTypeChange}
                  fullWidth
                  sx={{
                    '& .MuiToggleButtonGroup-grouped': {
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      '&:first-of-type': {
                        borderRadius: '8px 0 0 8px',
                      },
                      '&:last-of-type': {
                        borderRadius: '0 8px 8px 0',
                      },
                      '&:not(:first-of-type)': {
                        borderLeft: '1px solid rgba(255, 255, 255, 0.12)',
                      },
                    },
                  }}
                >
                  <ToggleButton
                    value="STRAIGHT"
                    sx={{
                      flex: 1,
                      color: '#F2F2F3',
                      fontFamily: '"Instrument Sans", sans-serif',
                      fontWeight: 400,
                      fontSize: '13.33px',
                      textTransform: 'none',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.16)',
                        color: '#F2F2F3',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                    }}
                  >
                    Straight
                  </ToggleButton>
                  <ToggleButton
                    value="PARLAY"
                    disabled={!canParlay}
                    sx={{
                      flex: 1,
                      color: canParlay ? '#F2F2F3' : 'rgba(242, 242, 243, 0.38)',
                      fontFamily: '"Instrument Sans", sans-serif',
                      fontWeight: 400,
                      fontSize: '13.33px',
                      textTransform: 'none',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.16)',
                        color: '#F2F2F3',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                    }}
                  >
                    Parlay
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Warning if parlay not available */}
                {!canParlay && betSlip.length >= 2 && (
                  <Typography
                    sx={{
                      fontFamily: '"Instrument Sans", sans-serif',
                      fontWeight: 400,
                      fontSize: '13.33px',
                      color: '#FFA726',
                      textAlign: 'center',
                    }}
                  >
                    Parlays only available on the same sportsbook
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Scrollable Bet Cards Section */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            minHeight: 0,
          }}
        >
          <Stack spacing={1}>
            {betType === 'PARLAY' && canParlay ? (
              <ParlayCard
                bets={betSlip}
                combinedOdds={combinedOdds}
                trueProb={trueProb}
                parlayEv={parlayEv}
                onRemove={handleRemove}
                onClearAll={handleClearSlip}
              />
            ) : (
              betSlip.map((bet, index) => {
                const betUrl = getBetDeepLink(bet);
                return (
                  <BetCard
                    key={bet.id}
                    bet={bet}
                    onRemove={() => handleRemove(index)}
                    onOpenSportsbook={betUrl ? () => handleOpenSportsbook(betUrl) : undefined}
                  />
                );
              })
            )}
          </Stack>
        </Box>

        {/* SGP Correlation Disclaimer - shown above button when parlay */}
        {betType === 'PARLAY' && canParlay && (
          <Typography
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 400,
              fontSize: '11px',
              fontStyle: 'italic',
              color: 'rgba(242, 242, 243, 0.5)',
              textAlign: 'center',
              px: 2,
              pb: 1,
            }}
          >
            Note: Model Win % and EV do not account for SGP correlation.
          </Typography>
        )}

        {/* Actions */}
        {betSlip.length > 0 && (
          <Box sx={{ p: 1, flexShrink: 0 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<OpenInNewIcon size={16} />}
              onClick={() => openBetSlipDeepLinks(betSlip)}
              sx={{
                height: 46.62,
                borderRadius: '6.67px',
                backgroundColor: '#42A5F5',
                border: '1px solid rgba(42, 83, 195, 0.3)',
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '13.33px',
                color: 'rgba(0, 0, 0, 0.87)',
                textTransform: 'none',
                boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.06), 0px 9px 9px 0px rgba(0, 0, 0, 0.05), 0px 21px 13px 0px rgba(0, 0, 0, 0.03), 0px 38px 15px 0px rgba(0, 0, 0, 0.01), 0px 59px 16px 0px rgba(0, 0, 0, 0)',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              {getPlaceButtonText()}
            </Button>
          </Box>
        )}

        {/* Empty state */}
        {betSlip.length === 0 && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ py: 6, px: 2 }}
          >
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                color: 'rgba(242, 242, 243, 0.5)',
                textAlign: 'center',
              }}
            >
              No bets in your slip yet.
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '13.33px',
                color: 'rgba(242, 242, 243, 0.38)',
                textAlign: 'center',
                mt: 1,
              }}
            >
              Tap "Add to Bet Slip" on any prop to get started.
            </Typography>
          </Stack>
        )}
      </Box>
    </SwipeableDrawer>
  );
};

export default BetSlipBottomSheet;
